/**
 * Layout Component
 *
 * Main layout wrapper with sidebar and header.
 * Uses Zustand for all UI state (sidebar, auth).
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "@/shared/layout/sidebar";
import { MobileBottomNav } from "@/shared/layout/MobileBottomNav";
import { NetworkConnectionStatus } from "@/shared/components/network-status";
import { NetworkMonitor } from "@/shared/components/NetworkMonitor";
import { useBoundStore } from "@/stores";
import { SidebarInset } from "@/shared/ui/sidebar";
import { GlobalHeader } from "./GlobalHeader";
import { PublicHeader } from "./PublicHeader";
import { SidebarResponsiveHandler } from "@/stores/hooks";
import { authService } from "@/features/auth/api/auth.service";
import i18n, { preInitLanguage } from "@/lib/i18n";

type Props = {
  noSidebar?: boolean;
  component: React.ReactElement;
};

const Layout = ({ noSidebar, component }: Props) => {
  const { t } = useTranslation('common');
  const isAuthenticated = useBoundStore.use.isAuthenticated();
  const isMobile = useBoundStore.use.isMobile();
  const setSupportedLanguages = useBoundStore.use.setSupportedLanguages();
  const setLanguage = useBoundStore.use.setLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    authService
      .getSupportedLanguages()
      .then((list) => {
        if (cancelled) return;
        const hadNoList = !useBoundStore.getState().supportedLanguagesList;
        setSupportedLanguages(list);
        if (list?.length) {
          const codes = list.map((l) => l.code);
          i18n.options.supportedLngs = codes;
          const languageUtils = (i18n as { services?: { languageUtils?: { supportedLngs: string[] } } }).services?.languageUtils;
          if (languageUtils) languageUtils.supportedLngs = codes;
          i18n.loadLanguages(codes).catch(() => { });
          const defaultFromApi = list.find((l) => l.isDefault)?.code;
          if (hadNoList) {
            const storeLanguage = useBoundStore.getState().currentLanguage;
            const targetLanguage =
              codes.includes(storeLanguage) && storeLanguage !== i18n.language
                ? storeLanguage
                : preInitLanguage && codes.includes(preInitLanguage)
                  ? preInitLanguage
                  : defaultFromApi;

            if (targetLanguage && targetLanguage !== i18n.language) {
              setLanguage(targetLanguage);
              i18n.changeLanguage(targetLanguage);
            }
          }
        }
      })
      .catch(() => {
        if (!cancelled) setSupportedLanguages(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setSupportedLanguages, setLanguage]);

  // No sidebar: use app header when authenticated (e.g. reset-password), public header for guest pages
  // h-dvh + overflow-hidden so only main scrolls; header stays fixed and content never scrolls above it (100dvh avoids iOS toolbar clipping vs 100vh)
  if (noSidebar) {
    return (
      <div className="h-dvh w-full bg-background text-foreground flex flex-col overflow-hidden">
        {isAuthenticated ? <GlobalHeader /> : <PublicHeader />}
        <main className="flex-1 flex flex-col min-h-0 overflow-auto">
          {component}
        </main>
        <NetworkConnectionStatus />
        <NetworkMonitor />
      </div>
    );
  }

  return (
    <>
      {/* Zustand sync handlers (headless components) */}
      <NetworkMonitor />
      <SidebarResponsiveHandler />

      {/* Layout structure - Full Width Header */}
      <div className="flex flex-col h-dvh w-full bg-sidebar text-foreground overflow-hidden">
        {/* Row 1: Global Header (Full Width) */}
        <GlobalHeader />

        {/* Row 2: Sidebar (desktop only) + Main Content */}
        <div className="flex flex-1 overflow-hidden relative">
          {isAuthenticated && !isMobile && <Sidebar className="border-e-0" />}

          <SidebarInset className="flex flex-col flex-1 bg-sidebar min-w-0 min-h-0">
            {/* Main content area: thin sides and bottom on mobile for max content space; minimal bottom padding for bottom nav */}
            <div className="flex flex-1 flex-col overflow-hidden px-2 sm:px-3 pb-0 lg:px-0 lg:pe-2 lg:pb-1">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md bg-background shadow-sm">
                {/* overflow-hidden so scroll stays inside each page (no scrollbar over header/nav on mobile). Safe for desktop: BookAppointments, ManageAppointments, MyAccount all use h-full/flex-1 + inner overflow-auto. */}
                <main id="main-scrollable-container" className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  {component}
                </main>
                {/* Mobile: inset inside rounded card (avoids a separate strip below the card); gray-50 matches ApplicationForm footer/chrome */}
                <div
                  className="min-h-[calc(4rem+env(safe-area-inset-bottom,0px))] shrink-0 bg-gray-50 lg:hidden"
                  aria-hidden
                />
              </div>
            </div>

            {/* Copyright Footer - in the navy section (hidden on mobile when bottom nav is shown) */}
            <footer className="hidden lg:block bg-sidebar text-center py-0.5 text-[9px] text-sidebar-foreground px-1 font-semibold">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </footer>

            <NetworkConnectionStatus />
          </SidebarInset>
        </div>

        {/* Mobile: app-style bottom navigation (only when authenticated + small screen) */}
        {isAuthenticated && isMobile && <MobileBottomNav />}
      </div>
    </>
  );
};

export default Layout;